import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { BRAND, BOOK_DEMO_URL, GET_STARTED_URL } from '../data/dgpt';
import { VC_STAGES } from '../data/platform';
import { VC_FAQ } from '../data/ventureContent';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { FaqList } from '../components/ui/FaqList';
import { ProcessFlow } from '../components/platform/ProcessFlow';
import { ProcessCta } from '../components/platform/ProcessCta';
import { VideoFacade } from '../components/dgpt/VideoFacade';
import { VcCapabilities } from '../components/venture/VcCapabilities';
import { InstitutionalMemory } from '../components/home/InstitutionalMemory';
import { SampleOutputSection } from '../components/home/SampleOutputSection';
import { EvidenceSection } from '../components/home/EvidenceSection';
import { ToolsGallery } from '../components/home/ToolsGallery';
import { MobileStickyCta } from '../components/dgpt/MobileStickyCta';
import { CustomizeWorkflowBand } from '../components/solutions/CustomizeWorkflowBand';
const DESCRIPTION = 'DiligenceGPT™ for venture capital: AI agents for deal intake, thesis fit, Deal Radar, structured diligence, IC memos and portfolio monitoring. Diligence at the Speed of Dealflow.';
export function VentureCapital() {
  usePageMeta({
    title: 'DiligenceGPT™ for Venture Capital | AI That Runs Your Dealflow',
    description: DESCRIPTION,
    path: '/venture-capital',
    keywords: ['AI due diligence for venture capital', 'investment thesis screening', 'AI investment memo generator', 'VC deal flow software', 'portfolio monitoring'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Venture Capital',
        item: canonicalUrl('/venture-capital')
      }]
    }]
  });
  return <>
      <main id="main" data-id="element-2164" data-mp="1oq32s6sd2ybj">
        <PageHero headingId="vc-page-heading" breadcrumb="Venture Capital" kicker="DiligenceGPT for Venture Capital" title={<>
              AI That Runs <em data-id="element-2166" data-mp="10u8icnzymx4s">Your Dealflow.</em>
            </>} description={<>
              From the first company in your inbox to investment committee and portfolio monitoring.
              <span className="mt-3 block font-semibold text-night-text" data-id="element-2167" data-mp="19v6n58i838of">{BRAND.tagline}</span>
            </>} actions={<>
              <CtaButton href={GET_STARTED_URL} variant="violet" size="xl" event="primary_cta_click" eventParams={{
          placement: 'vc_hero',
          cta: 'run_first_deal'
        }} className="w-full sm:w-auto" data-id="element-2168" data-mp="1evktmm46eakc">
                Run Your First Deal
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2169" data-mp="2veacyj8dqs8o" />
              </CtaButton>
              <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
          placement: 'vc_hero',
          cta: 'see_on_thesis'
        }} className="w-full sm:w-auto" data-id="element-2170" data-mp="2qn23jn71v6vi">
                See DGPT on Your Thesis
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2171" data-mp="2cly1n6zr0gme" />
              </CtaButton>
            </>} aside={<VideoFacade data-id="element-2172" data-mp="27q99hoq26204" />} data-id="element-2165" data-mp="1tiw6crc3g0m1" />

        <section aria-labelledby="vc-workflow-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2173" data-mp="23ncppuos7zul">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-2174" data-mp="5w1mhesjuldt">
            <SectionHeading eyebrow="The venture workflow" headingId="vc-workflow-heading" title="Seven stages. Specialized agents underneath each one." description="Your team owns every decision point. Agents handle the intake, research, follow-up and preparation in between." data-id="element-2175" data-mp="1v7zcnmxlalz" />
            <Reveal className="mt-12" data-id="element-2176" data-mp="2gmgfllann">
              <ProcessFlow steps={VC_STAGES} ariaLabel="Venture capital workflow with agents" data-id="element-2177" data-mp="3z1tcn7kmbpn" />
            </Reveal>
          </div>
        </section>

        <VcCapabilities data-id="element-2178" data-mp="3mc9hcc5ul1mv" />

        <section className="dgpt-dark bg-night" aria-labelledby="memory-heading" data-id="element-2179" data-mp="3r4aq3o3t4a85">
          <InstitutionalMemory headingLevel="h2" data-id="element-2180" data-mp="3r6dlknnqavk9" />
        </section>

        <SampleOutputSection data-id="element-2181" data-mp="3cgppq1ybgp3x" />
        <EvidenceSection data-id="element-2182" data-mp="3cdjwqbmkh32x" />
        <ToolsGallery data-id="element-2183" data-mp="3he7v5id4km76" />
        <CustomizeWorkflowBand template="vc" placement="vc" data-id="element-2184" data-mp="3hfj21r1vaa2i" />

        <section aria-labelledby="vc-faq-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2185" data-mp="33eq2grwpkosj">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-8 lg:py-24" data-id="element-2186" data-mp="38djkmxmxx3z1">
            <SectionHeading eyebrow="FAQ" headingId="vc-faq-heading" title="Questions from venture teams." data-id="element-2187" data-mp="2osj8yeex8479" />
            <Reveal data-id="element-2188" data-mp="2jurbz5zf5rw7">
              <FaqList items={VC_FAQ} data-id="element-2189" data-mp="2xspl9wyvuy4u" />
            </Reveal>
          </div>
        </section>

        <ProcessCta placement="vc" title="Run Your First Deal." body="Bring a pitch deck and your thesis. See how DiligenceGPT structures the work around your decision." primaryLabel="Run Your First Deal" data-id="element-2190" data-mp="2xqcvwyqsplhr" />
      </main>
      <MobileStickyCta placement="vc_mobile_sticky" data-id="element-2191" data-mp="32r98s1t4khzb" />
    </>;
}